import axios, { AxiosError } from 'axios';
import { ApiType, ApiConfig } from './ApiClient.types';
import { ERROR_MESSAGES } from './ApiClient.constants';

/**
 * Extracts API configuration from Redux state
 */
export const extractApiConfig = (state: any): ApiConfig => ({
  apimContentHub: state.application.apimContentHub,
  apimBaseUrl: state.application.apimBaseUrl,
  apimFormApi: state.application.apimFormApi,
  apimMotorApi: state.application.apimMotorApi,
  apimNafNoApi: state.application.apimNafNoApi,
  apimVehicleDataApi: state.application.apimVehicleDataApi,
  apimMembershipApi: state.application.apimMembershipApi,
  apimNafCenterApi: state.application.apimNafCenterApi,
  apimTravelApi: state.application.apimTravelApi,
  apimMapsApi: state.application.apimMapsApi,
  elasticSearchAPI: state.application.elasticSearchAPI,
});

/**
 * Gets the endpoint URL for a specific API type
 */
export const getEndpointUrl = (apiType: ApiType, config: ApiConfig): string => {
  if (apiType === ApiType.external) {
    return '';
  }

  const endpoint = config[apiType];
  if (!endpoint) {
    throw new Error(ERROR_MESSAGES.API_ENDPOINT_NOT_FOUND(apiType));
  }
  return endpoint;
};

/**
 * Gets the base URL from configuration
 */
export const getBaseUrl = (config: ApiConfig): string => {
  if (!config.apimBaseUrl) {
    throw new Error(ERROR_MESSAGES.REQUIRED_CONFIG_NOT_FOUND);
  }
  return config.apimBaseUrl;
};

/**
 * Prepares request headers with authentication if available
 */
export const prepareRequestHeaders = (
  headers: Record<string, string> = {},
  config: ApiConfig,
): Record<string, string> => {
  const requestHeaders: Record<string, string> = {
    ...headers,
  };

  if (config.apimContentHub) {
    requestHeaders['Ocp-Apim-Subscription-Key'] = config.apimContentHub;
  }

  if (config.authToken) {
    requestHeaders.Authorization = `Bearer ${config.authToken}`;
  }

  return requestHeaders;
};

export type ClientPlatform = 'android' | 'ios' | 'mobile' | 'windows' | 'mac' | 'desktop' | 'unknown';

export const getClientPlatform = (userAgent?: string): ClientPlatform => {
  const resolvedUserAgent =
    userAgent || (typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '');
  const normalizedUserAgent = resolvedUserAgent.toLowerCase();

  if (!normalizedUserAgent) {
    return 'unknown';
  }

  if (normalizedUserAgent.includes('android')) {
    return 'android';
  }

  if (
    normalizedUserAgent.includes('iphone') ||
    normalizedUserAgent.includes('ipad') ||
    normalizedUserAgent.includes('ipod')
  ) {
    return 'ios';
  }

  if (
    normalizedUserAgent.includes('mobile') ||
    normalizedUserAgent.includes('windows phone') ||
    normalizedUserAgent.includes('blackberry')
  ) {
    return 'mobile';
  }

  if (normalizedUserAgent.includes('windows')) {
    return 'windows';
  }

  if (normalizedUserAgent.includes('macintosh') || normalizedUserAgent.includes('mac os')) {
    return 'mac';
  }

  return 'desktop';
};

export const isAxiosError = <ResponseType>(error: unknown): error is AxiosError<ResponseType> =>
  axios.isAxiosError(error);
